import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { marketLedgerStatusSchema, stockCountKindSchema, stockCountScanKindSchema, stockCountStatusSchema, marketUnitCostSchema, } from "./enums.js";
/**
 * Stok sayımı (Prompt 16, ADR 0016 §13). Açılışta kapsamdaki her ürünün sistem miktarı ve ortalama maliyeti
 * DONDURULUR (fotoğraf). Sayılan = okutmaların toplamı (elle düzeltme de bir okutmadır). Fark = sayılan −
 * (fotoğraf + fotoğraftan satırın son okutma anına kadarki hareketler) → sayım sürerken yapılan satış fark doğurmaz.
 * Onay `market.count.approve` + step-up; net fark eşiği aşarsa onay motoru `STOCK_COUNT`.
 */
export const stockCountLineSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    barcode: z.string().nullable().describe("Ana barkod"),
    snapshotQuantity: quantitySchema,
    snapshotUnitCost: marketUnitCostSchema,
    countedQuantity: quantitySchema.nullable().describe("Okutmaların toplamı; hiç okutulmadıysa null"),
    countedAt: timestampSchema.nullable().describe("Son okutma anı"),
    movementsSinceSnapshot: quantitySchema.nullable(),
    expectedQuantity: quantitySchema.nullable(),
    differenceQuantity: quantitySchema.nullable().describe("Sayılan − beklenen (negatif = noksan)"),
    differenceValue: moneySchema
        .nullable()
        .describe("Gönderimde fotoğraf maliyetiyle, onayda gerçek hareket değeriyle"),
    note: z.string().nullable(),
});
export const stockCountSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    countNo: z.string(),
    kind: stockCountKindSchema,
    scope: z.object({
        categoryIds: z.array(uuidSchema),
        productIds: z.array(uuidSchema),
    }),
    status: stockCountStatusSchema,
    snapshotAt: timestampSchema,
    submittedAt: timestampSchema.nullable(),
    submittedBy: uuidSchema.nullable(),
    approvedAt: timestampSchema.nullable(),
    approvedBy: uuidSchema.nullable(),
    approvalRequestId: uuidSchema.nullable(),
    approvalPending: z.boolean().describe("Onay motorunda bekleyen talep var"),
    shortageValue: moneySchema,
    surplusValue: moneySchema,
    netValue: moneySchema.describe("Fazla − noksan (kuruş)"),
    lineCount: z.number().int(),
    countedLineCount: z.number().int(),
    journalEntryId: uuidSchema.nullable(),
    ledgerStatus: marketLedgerStatusSchema,
    note: z.string().nullable(),
    lines: z.array(stockCountLineSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const stockCountPageSchema = pageOf(stockCountSchema);
export const listStockCountsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: stockCountStatusSchema.optional(),
    kind: stockCountKindSchema.optional(),
});
export const createStockCountSchema = z
    .object({
    warehouseId: uuidSchema,
    kind: stockCountKindSchema,
    categoryIds: z.array(uuidSchema).max(200).default([]).describe("Kısmi sayım: kategoriler"),
    productIds: z
        .array(uuidSchema)
        .max(5000)
        .default([])
        .describe("Kısmi / döngüsel sayım: ürünler (döngüselde boşsa öneri kullanılır)"),
    cycleLimit: z.number().int().min(1).max(500).default(30).describe("Döngüsel öneri en çok kaç ürün"),
    note: z.string().trim().max(1000).nullable().default(null),
})
    .refine((value) => value.kind !== "PARTIAL" || value.categoryIds.length > 0 || value.productIds.length > 0, { message: "Kısmi sayımda en az bir kategori ya da ürün seçin", path: ["productIds"] });
export const stockCountScanSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    barcode: z.string().nullable(),
    quantity: quantitySchema,
    kind: stockCountScanKindSchema,
    scannedAt: timestampSchema,
    scannedBy: uuidSchema.nullable(),
    device: z.string().nullable(),
    countedQuantity: quantitySchema.describe("Okutmadan sonra satırın sayılanı"),
});
/**
 * Okutma: `SCAN` barkodla (+ miktar), `MANUAL` elle girilen ürün (+ miktar), `CORRECTION` ± düzeltme.
 * Kapsam dışı ürün 422 `STOCK_COUNT_OUT_OF_SCOPE`.
 */
export const createStockCountScanSchema = z
    .object({
    kind: stockCountScanKindSchema.default("SCAN"),
    barcode: z.string().trim().min(4).max(48).optional(),
    productId: uuidSchema.optional(),
    quantity: quantitySchema
        .refine((value) => Number(value) !== 0, "Miktar sıfır olamaz")
        .default("1")
        .describe('Okutmada varsayılan "1"; düzeltmede ± fark'),
    device: z.string().trim().max(60).nullable().default(null).describe("Tablet / el terminali adı"),
})
    .refine((value) => value.barcode !== undefined || value.productId !== undefined, {
    message: "Barkod ya da ürün verin",
    path: ["barcode"],
})
    .refine((value) => value.kind === "CORRECTION" || Number(value.quantity) > 0, {
    message: "Okutma miktarı pozitif olmalıdır (eksiltme için düzeltme kullanın)",
    path: ["quantity"],
});
/** Satırın sayılanını doğrudan ayarlar: fark kadar `CORRECTION` okutması yazılır (okutmalar silinmez). */
export const setStockCountLineSchema = z.object({
    countedQuantity: quantitySchema.refine((value) => Number(value) >= 0, "Sayılan miktar negatif olamaz"),
    note: z.string().trim().max(300).nullable().optional(),
});
export const submitStockCountSchema = z.object({
    version: z.number().int().positive(),
    uncountedAsZero: z
        .boolean()
        .default(false)
        .describe("Hiç okutulmamış satırlar SIFIR sayılsın mı (tam sayımda rafta olmayan ürün)"),
});
export const stockCountActionSchema = z.object({
    version: z.number().int().positive(),
    note: z.string().trim().max(1000).nullable().optional(),
});
// ── Döngüsel sayım önerisi ───────────────────────────────────────────────────
export const cycleCountSuggestionsQuerySchema = z.object({
    warehouseId: uuidSchema,
    limit: z.coerce.number().int().min(1).max(500).default(30),
});
export const cycleCountSuggestionSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    quantity: quantitySchema,
    stockValue: moneySchema,
    lastCountedAt: timestampSchema.nullable(),
    daysSinceLastCount: z.number().int().nullable().describe("Hiç sayılmadıysa null"),
});
export const cycleCountSuggestionListSchema = z.object({
    staleDays: z.number().int(),
    items: z.array(cycleCountSuggestionSchema),
});
export const stockCountLineParamsSchema = z.object({ id: uuidSchema, lineId: uuidSchema });
//# sourceMappingURL=counts.js.map